import { Link } from '@tanstack/react-router'
import { ClipboardCheck, Construction } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import { LEDGER_CAPABILITY_GAPS } from './constants'

/**
 * What the ledger is catalogued to do and this application still cannot do, said once and said
 * precisely.
 *
 * The list is short now — most of what it used to name has endpoints behind it — and it is kept for
 * the same reason it was written. A back-office accountant who cannot find a control has to be able
 * to tell three things apart: they lack a permission, the figure is zero, or the capability does not
 * exist. Only naming the third prevents the other two readings, and nothing in this module shows a
 * plausible-looking empty table in place of something that was never served. In a banking
 * application a figure on screen is a claim, and a reader cannot tell an invented one from a real
 * one.
 */
export function LedgerCapabilityGaps() {
  // Nothing to declare renders nothing: an empty "still not here" card would read as a screen
  // that lost its content rather than a module that closed its gaps.
  if (LEDGER_CAPABILITY_GAPS.length === 0) {
    return null
  }

  return (
    <Card className="border-dashed">
      <CardHeader>
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2">
            <Construction className="size-4 text-muted" aria-hidden />
            Still not here
          </CardTitle>
          <p className="mt-0.5 text-xs text-muted">
            Each of these is a gap in the API or in the permission catalogue, not in your access and
            not in this screen.
          </p>
        </div>
      </CardHeader>

      <CardContent>
        <dl className="space-y-4">
          {LEDGER_CAPABILITY_GAPS.map((gap) => (
            <div key={gap.title} className="min-w-0">
              <dt className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-heading">{gap.title}</span>
                <Badge tone="neutral" className="font-mono">
                  {gap.permission}
                </Badge>
              </dt>
              <dd className="mt-0.5 text-xs text-body">{gap.detail}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  )
}

/**
 * Where the ledger's countersigned work goes.
 *
 * Four ledger operations answer with an approval envelope — a manual journal, a reversal, a period
 * reopening, and the business day close — and a request is filed under the permission code of the
 * operation itself, so ledger requests are identifiable as `ledger.*` in the inbox.
 *
 * Two facts here are operational rather than decorative. A journal request carries a twelve-hour
 * window rather than the institution's usual forty-eight, so one raised at four in the afternoon
 * lapses before the next working afternoon. And `awaitingApproval` can legitimately be false —
 * approval policies are configurable rows and an institution may switch one off — which is why every
 * screen here reads the envelope rather than announcing "sent for approval" on the strength of the
 * command having been countersignable.
 */
export function LedgerApprovalRouting() {
  return (
    <Alert tone="info">
      <ClipboardCheck aria-hidden />
      <div className="space-y-2">
        <AlertTitle>Countersigned ledger work goes to the approvals queue</AlertTitle>
        <AlertDescription>
          Manual journals, reversals, period reopenings and business day closes may each come back as
          a stored request rather than as work that has happened, and every screen in this module says
          which. A request is filed under the permission code of the operation itself, so the ledger&apos;s
          are the ones beginning <span className="font-mono text-xs">ledger.</span> A journal request
          expires twelve hours after it is made — half the institution&apos;s usual window.
        </AlertDescription>
        <Button asChild variant="secondary" size="sm">
          <Link to="/approvals">Go to approvals</Link>
        </Button>
      </div>
    </Alert>
  )
}
